import { createContext, PropsWithChildren, RefObject, useContext, useRef } from "react"

import { Footer } from "~/shared/ui/layouts/main/Footer"
import { Topbar } from "~/shared/ui/layouts/main/Topbar"
import { Header } from "./Header"
import { Logo } from "./Logo"
import { Sidebar } from "./Sidebar"
import type { LayoutProps } from "./types"
import { Userbar } from "./Userbar"

import mainGlobalCss from "~/shared/assets/main.global.scss?inline"
import { GlobalStyle } from "~/shared/ui/components"

import cssModule from "./index.module.scss"
import { cx } from "classix"

import "~/shared/ui/kit/theme.default.css"
import "~/shared/ui/kit/index.css"
import { useMediaQuery } from "react-responsive"
import { breakPoints } from "~/shared/ui/constants"

const ContentContainerContext = createContext<{
	contentContainerRef: RefObject<HTMLDivElement | null>
}>({
	contentContainerRef: { current: null }
})

export function useContentContainerContext() {
	return useContext(ContentContainerContext)
}

/**
 * Главный макет
 */
export function Layout({
						   logo: {
							   onClick,
							   customization = {},
							   showMenuButton = false,
							   url: logoUrl = "/main",
							   text: logoText = "ЭИОС",
							   onMenuButtonClick
						   } = {},
						   header: {
							   org = 'Кемеровский государственный университет',
							   orgUrl = "https://kemsu.ru",
							   title = 'Электронная информационно-образовательная среда',
							   titleUrl = "/main",
							   subtitle
						   } = {},
						   userbar: { render = () => <></> } = {},
						   topbar: {
							   breadcrumbs = [],
							   hide: topbarHide = false,
							   additionalInfo: topbarAdditionalInfo = ""
						   } = {},
						   sidebar: {
							   enableTransition,
							   onDarkAreaClick,
							   customization: sidebarStyle = {},
							   hide: sidebarHide = false,
							   links: sidebarLinks = [],
							   backUrl
						   } = {},
						   content: { wrapperProps = {}, title: contentTitle } = {},
						   footer: { content: footerContent } = {},
						   children
					   }: PropsWithChildren<LayoutProps>) {

	const sidebarBackUrl =
		backUrl ??
		breadcrumbs?.at(-2)?.url ??
		(location.pathname.split("/").slice(0, -1).join("/") || "/main")

	const contentContainerRef = useRef<HTMLDivElement>(null)

	const isSmallDevice = useMediaQuery({ maxWidth: breakPoints.sm })

	return (
		<>
			<GlobalStyle>{mainGlobalCss}</GlobalStyle>
			<div className={cx(cssModule.layoutGrid, enableTransition && cssModule.transition)}>
				<Logo
					onClick={onClick}
					enableTransition={enableTransition ?? false}
					showMenuButton={showMenuButton}
					onMenuButtonClick={onMenuButtonClick}
					logoUrl={logoUrl}
					logoText={logoText}
					hideCurtain={sidebarHide}
					customization={customization}
				/>
				<Header org={org} orgUrl={orgUrl} title={title} titleUrl={titleUrl} subtitle={subtitle} />
				<Userbar element={render()}
						 showMenuButton={showMenuButton}
						 hideCurtain={sidebarHide}
						 customization={customization}
						 onMenuButtonClick={onMenuButtonClick}/>

				<div className={cx(cssModule.layoutBottom, enableTransition && cssModule.transition, sidebarHide
					? cssModule.hideSidebarColInLayout
					: cssModule.showSidebarColInLayout)}>
					<Sidebar
						onLogoClick={onClick}
						onDarkAreaClick={onDarkAreaClick}
						links={sidebarLinks}
						hide={sidebarHide}
						backUrl={sidebarBackUrl}
						customization={sidebarStyle}
						logoText={logoText}
						logoUrl={logoUrl}
						enableTransition={enableTransition ?? false}
					/>
					<Topbar
						breadcrumbs={breadcrumbs}
						hide={topbarHide}
						additionalInfo={topbarAdditionalInfo}
						backUrl={sidebarBackUrl}
					/>
					<div className={cssModule.contentFooterContainer} ref={contentContainerRef}>
						<main className={cssModule.content} {...wrapperProps}>
							{!isSmallDevice && contentTitle && <h1>{contentTitle}</h1>}
							<ContentContainerContext.Provider value={{ contentContainerRef }}>
								{children}
							</ContentContainerContext.Provider>
						</main>
						<Footer content={footerContent} />
					</div>
				</div>
			</div>
		</>
	)
}
